/**
 * Shapermint Theme Configuration
 * Official Shapermint brand theme with complete styling
 */

'use client';

export const shapermintTheme = {
  name: 'Shapermint',
  fontSizes: [10, 12, 14, 16, 18, 20, 24, 30, 36, 42, 52, 60, 68, 72, 76],
  fontWeights: [400, 600, 500, 700, 800],
  lineHeights: [14, 16, 18, 20, 22, 24, 28, 32, 38, 44, 52, 68, 76, 84, 88, 92],
  mediaQueries: {
    mobile: 640,
    tablet: 1024,
    desktop: 1280,
    xs: '480px',
    sm: '640px',
    md: '768px',
    lg: '1024px',
    xl: '1280px',
    '2xl': '1536px',
  },
  radius: {
    small: '4px',
    regular: '8px',
    full: '9999px',
  },
  zIndex: {
    modal: 50,
    overlay: 49,
    toast: 60,
  },
  colors: {
    pallete: {
      primary: {
        color: '#f7a08b',
        contrast: '#292929',
        soft: {
          color: '#fcd9d1',
          contrast: '#292929',
        },
      },
      secondary: {
        color: '#292929',
        contrast: '#ffffff',
        soft: {
          color: '#bbbbbb',
          contrast: '#292929',
        },
      },
      wine: {
        color: '#882A2B',
        contrast: '#ffffff',
      },
      red: {
        color: '#D3373C',
        contrast: '#ffffff',
      },
      creamy: {
        color: '#FFE9D8',
        contrast: '#292929',
      },
      ocean: {
        color: '#2B526D',
        contrast: '#ffffff',
      },
    },
    shades: {
      black: { color: '#000', contrast: '#fff' },
      white: { color: '#fff', contrast: '#292929' },
      100: { color: '#e5e5e5', contrast: '#292929' },
      200: { color: '#bbbbbb', contrast: '#292929' },
      250: { color: '#a6a6a6', contrast: '#292929' },
      300: { color: '#9e9e9e', contrast: '#292929' },
      400: { color: '#808080', contrast: '#ffffff' },
      500: { color: '#5a5a5a', contrast: '#ffffff' },
      550: { color: '#3a3a3a', contrast: '#ffffff' },
      700: { color: '#292929', contrast: '#ffffff' },
    },
    semantic: {
      positive: { color: '#2f806a', contrast: '#ffffff' },
      urgent: { color: '#c64844', contrast: '#ffffff' },
      attention: { color: '#f2d96f', contrast: '#292929' },
      informative: { color: '#0a609b', contrast: '#ffffff' },
      warning: { color: '#fbbf24', contrast: '#292929' }, // Gold/amber for stars
    },
    border: {
      color: '#e5e5e5',
      disabled: '#bbbbbb',
      highlight: '#292929',
    },
    background: {
      color: '#ffffff',
      disabled: '#efefef',
      primary: '#ffffff',
      secondary: '#f5f5f5',
    },
    text: {
      color: '#292929',
      disabled: '#a6a6a6',
      primary: '#292929',
      secondary: '#808080',
      tertiary: '#5a5a5a',
    },
    primary: '#f7a08b',
    secondary: '#292929',
  },
  component: {
    button: {
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: '600',
      lineHeight: 'normal',
      letterSpacing: 'normal',
      minWidth: '120px',
      minHeight: '44px',
      size: {
        small: {
          fontSize: '0.75rem',
          padding: '0.625rem 2rem',
        },
        medium: {
          fontSize: '0.875rem',
          padding: '0.688rem 2rem',
        },
        large: {
          fontSize: '1rem',
          padding: '0.875rem 2rem',
        },
      },
      primary: {
        backgroundColor: '#f7a08b',
        color: '#292929',
        hoverBackgroundColor: '#fcd9d1',
        active: {
          backgroundColor: '#f7a08b',
          color: '#292929',
        },
        hover: {
          backgroundColor: '#fcd9d1',
          color: '#292929',
        },
      },
      secondary: {
        backgroundColor: '#292929',
        color: '#ffffff',
        hoverBackgroundColor: '#545454',
        active: {
          backgroundColor: '#292929',
          color: '#ffffff',
        },
        hover: {
          backgroundColor: '#545454',
          color: '#ffffff',
        },
      },
      tertiary: {
        backgroundColor: 'transparent',
        color: '#292929',
        hoverBackgroundColor: '#f5f5f5',
        active: {
          backgroundColor: 'transparent',
          color: '#292929',
        },
        hover: {
          backgroundColor: '#f5f5f5',
          color: '#292929',
        },
      },
      outline: {
        backgroundColor: 'transparent',
        color: '#292929',
        borderColor: '#e5e5e5',
        hoverBackgroundColor: '#f5f5f5',
        hoverBorderColor: '#292929',
        active: {
          backgroundColor: 'transparent',
          color: '#292929',
          borderColor: '#e5e5e5',
        },
        hover: {
          backgroundColor: '#f5f5f5',
          color: '#292929',
          borderColor: '#292929',
        },
      },
      ghost: {
        backgroundColor: 'transparent',
        color: '#292929',
        hoverBackgroundColor: 'transparent',
        active: {
          backgroundColor: 'transparent',
          color: '#292929',
        },
        hover: {
          backgroundColor: 'transparent',
          color: '#5a5a5a',
        },
      },
      destructive: {
        backgroundColor: 'transparent',
        color: '#c64844',
        hoverBackgroundColor: 'transparent',
        active: {
          backgroundColor: 'transparent',
          color: '#c64844',
        },
        hover: {
          backgroundColor: 'transparent',
          color: '#a33a37',
        },
      },
      icon: {
        size: {
          sm: { padding: '0.375rem', fontSize: '0.875rem' },
          md: { padding: '0.5rem', fontSize: '1rem' },
          lg: { padding: '0.75rem', fontSize: '1.25rem' },
        },
      },
      chip: {
        backgroundColor: '#ffffff',
        color: '#292929',
        borderColor: '#e5e5e5',
        hoverBackgroundColor: '#f5f5f5',
        active: {
          backgroundColor: '#292929',
          color: '#ffffff',
          borderColor: '#292929',
        },
        hover: {
          backgroundColor: '#f5f5f5',
          color: '#292929',
          borderColor: '#292929',
        },
      },
      tab: {
        backgroundColor: 'transparent',
        color: '#808080',
        borderColor: 'transparent',
        hoverBackgroundColor: 'transparent',
        active: {
          backgroundColor: 'transparent',
          color: '#292929',
          borderColor: '#f7a08b',
        },
        hover: {
          backgroundColor: 'transparent',
          color: '#292929',
          borderColor: 'transparent',
        },
      },
      link: {
        backgroundColor: 'transparent',
        color: '#292929',
        hoverBackgroundColor: 'transparent',
        active: {
          backgroundColor: 'transparent',
          color: '#292929',
        },
        hover: {
          backgroundColor: 'transparent',
          color: '#5a5a5a',
        },
      },
    },
    input: {
      borderRadius: '8px',
      borderWidth: '1px',
      transition: 'border-color 0.2s ease, box-shadow 0.2s ease',
      default: {
        backgroundColor: '#ffffff',
        color: '#292929',
        borderColor: '#e5e5e5',
        focusBorderColor: '#292929',
        errorBorderColor: '#d32f2f',
        placeholderColor: '#5a5a5a',
        disabledOpacity: 0.6,
      },
      search: {
        backgroundColor: '#ffffff',
        color: '#292929',
        borderColor: '#e5e5e5',
        borderWidth: '2px',
        focusBorderColor: '#f7a08b',
        focusRing: '0 0 0 3px rgba(247, 160, 139, 0.2)',
        errorBorderColor: '#d32f2f',
        placeholderColor: '#9ca3af',
        disabledOpacity: 0.6,
      },
      borderless: {
        backgroundColor: 'transparent',
        color: '#292929',
        borderColor: 'transparent',
        focusBorderColor: 'transparent',
        errorBorderColor: '#d32f2f',
        placeholderColor: '#5a5a5a',
        disabledOpacity: 0.6,
      },
      size: {
        sm: {
          fontSize: '0.875rem',
          padding: '0.5rem 0.75rem',
          minHeight: '36px',
        },
        md: {
          fontSize: '1rem',
          padding: '1rem 1.5rem',
          minHeight: '48px',
        },
      },
    },
    select: {
      borderRadius: '8px',
      default: {
        backgroundColor: '#ffffff',
        color: '#292929',
        borderColor: '#e5e5e5',
        focusBorderColor: '#292929',
        fontWeight: 'inherit',
        disabledOpacity: 0.6,
      },
      inline: {
        backgroundColor: '#f7f7f7',
        color: '#292929',
        borderColor: '#e5e5e5',
        focusBorderColor: '#292929',
        fontWeight: '600',
        disabledOpacity: 0.6,
      },
      size: {
        sm: {
          fontSize: '0.875rem',
          padding: '0.5rem 0.75rem',
          minHeight: '36px',
        },
        md: {
          fontSize: '1rem',
          padding: '1rem',
          minHeight: '48px',
        },
      },
    },
    checkbox: {
      accentColor: '#292929',
      borderColor: '#e5e5e5',
      checkedBackground: '#292929',
      checkedColor: '#ffffff',
      disabledOpacity: 0.5,
      size: {
        sm: { width: '14px', height: '14px' },
        md: { width: '18px', height: '18px' },
      },
      card: {
        backgroundColor: '#ffffff',
        borderColor: '#e5e5e5',
        selectedBorderColor: '#292929',
        selectedBackground: 'rgba(47, 128, 106, 0.08)',
        hoverBorderColor: '#292929',
        borderRadius: '8px',
        padding: '1rem',
      },
    },
    radio: {
      borderColor: '#e5e5e5',
      checkedBorderColor: '#292929',
      checkedFill: '#292929',
      disabledOpacity: 0.5,
      size: {
        sm: { width: '16px', height: '16px', inner: '8px' },
        md: { width: '18px', height: '18px', inner: '10px' },
      },
      card: {
        backgroundColor: '#ffffff',
        borderColor: '#e5e5e5',
        selectedBorderColor: '#292929',
        hoverBorderColor: '#292929',
        borderRadius: '8px',
        padding: '1rem',
      },
    },
    formField: {
      gap: '0.25rem',
      label: {
        bold: { fontWeight: 700, fontSize: '1rem' },
        semibold: { fontWeight: 600, fontSize: '0.875rem' },
        regular: { fontWeight: 400, fontSize: '0.875rem' },
      },
      error: {
        color: '#c64844',
        fontSize: '0.875rem',
      },
      requiredMark: {
        color: '#c64844',
      },
    },
    badge: {
      fontWeight: 700,
      letterSpacing: '0.05em',
      lineHeight: 1,
      intents: {
        urgent: {
          // #a33a37 on white ≈ 5.4:1 (WCAG AA)
          backgroundColor: '#a33a37',
          color: '#ffffff',
        },
        positive: {
          backgroundColor: '#2f806a',
          color: '#ffffff',
        },
        attention: {
          backgroundColor: '#f2d96f',
          color: '#292929',
        },
        dark: {
          backgroundColor: '#292929',
          color: '#ffffff',
        },
        light: {
          backgroundColor: '#ffffff',
          color: '#292929',
          borderColor: '#e5e5e5',
        },
        info: {
          // #2563eb on white ≈ 4.6:1 (WCAG AA)
          backgroundColor: '#2563eb',
          color: '#ffffff',
        },
        subtle: {
          success: {
            backgroundColor: 'rgba(47, 128, 106, 0.1)',
            color: '#2f806a',
          },
          error: {
            // #b42b2b on white ≈ 5.0:1 (WCAG AA)
            backgroundColor: 'rgba(197, 48, 48, 0.1)',
            color: '#b42b2b',
          },
          warning: {
            // #6b5a12 on white ≈ 7.0:1 (WCAG AA)
            backgroundColor: 'rgba(200, 170, 50, 0.1)',
            color: '#6b5a12',
          },
          info: {
            // #085490 on white ≈ 5.5:1 (WCAG AA)
            backgroundColor: 'rgba(10, 96, 155, 0.1)',
            color: '#085490',
          },
        },
      },
      sizes: {
        xs: {
          fontSize: '0.6875rem',
          padding: '1px 6px',
          iconSize: '10px',
          circle: { minWidth: '16px', height: '16px', padding: '0 4px' },
        },
        sm: {
          fontSize: '0.75rem',
          padding: '4px 10px',
          iconSize: '14px',
          circle: { minWidth: '18px', height: '18px', padding: '0 4px' },
        },
        md: {
          fontSize: '0.875rem',
          padding: '4px 12px',
          iconSize: '16px',
          circle: { minWidth: '20px', height: '20px', padding: '0 6px' },
        },
      },
      shapes: {
        pill: { borderRadius: '100px' },
        rounded: { borderRadius: '4px' },
        tab: { borderRadius: '0 4px 4px 0' },
        circle: { borderRadius: '50%' },
      },
    },
    skeleton: {
      baseColor: '#f5f5f5',
      highlightColor: '#ffffff',
      borderRadius: '4px',
      roundedBorderRadius: '8px',
      duration: '1.5s',
    },
    toast: {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      shadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
      padding: '16px',
      gap: '10px',
      autoDismissMs: 5000,
      variants: {
        success: { accentColor: '#2f806a', iconColor: '#2f806a' },
        error: { accentColor: '#b42b2b', iconColor: '#b42b2b' },
        info: { accentColor: '#085490', iconColor: '#085490' },
        warning: { accentColor: '#fbbf24', iconColor: '#92400e' },
      },
    },
    text: {
      variants: {
        heading1: {
          fontSize: '2.625rem',
          lineHeight: '3.25rem',
          fontWeight: 700,
          tag: 'h1',
        },
        heading2: {
          fontSize: '2.25rem',
          lineHeight: '2.75rem',
          fontWeight: 700,
          tag: 'h2',
        },
        heading3: {
          fontSize: '1.875rem',
          lineHeight: '2.375rem',
          fontWeight: 700,
          tag: 'h3',
        },
        heading4: {
          fontSize: '1.5rem',
          lineHeight: '2rem',
          fontWeight: 700,
          tag: 'h4',
        },
        heading5: {
          fontSize: '1.25rem',
          lineHeight: '1.75rem',
          fontWeight: 600,
          tag: 'h5',
        },
        heading6: {
          fontSize: '1.125rem',
          lineHeight: '1.5rem',
          fontWeight: 600,
          tag: 'h6',
        },
        body: {
          fontSize: '1rem',
          lineHeight: '1.5rem',
          fontWeight: 400,
          tag: 'p',
        },
        bodySmall: {
          fontSize: '0.875rem',
          lineHeight: '1.375rem',
          fontWeight: 400,
          tag: 'p',
        },
        caption: {
          fontSize: '0.75rem',
          lineHeight: '1rem',
          fontWeight: 400,
          tag: 'span',
        },
        captionSmall: {
          fontSize: '0.625rem',
          lineHeight: '0.875rem',
          fontWeight: 400,
          tag: 'span',
        },
        label: {
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          fontWeight: 600,
          tag: 'label',
        },
        labelCaps: {
          fontSize: '0.75rem',
          lineHeight: '1rem',
          fontWeight: 700,
          tag: 'span',
          textTransform: 'uppercase',
          letterSpacing: '0.5px',
        },
        error: {
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          fontWeight: 400,
          tag: 'span',
          color: '#c64844',
        },
        link: {
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          fontWeight: 600,
          tag: 'a',
        },
        button: {
          fontSize: '0.875rem',
          lineHeight: '1.5',
          fontWeight: 700,
          tag: 'span',
        },
      },
      weights: {
        regular: 400,
        semibold: 600,
        bold: 700,
        heavy: 900,
      },
      sizes: {
        sm: { scale: 0.875 },
        md: { scale: 1 },
        lg: { scale: 1.125 },
      },
    },
  },
  // Legacy typography tokens — kept for Rating/LoadMore and storefront
  // pages still reading theme.typography.variants directly.
  typography: {
    config: {
      weight: {
        heavy: 900,
        bold: 700,
        demi: 600,
        semibold: 600,
        regular: 400,
      },
    },
    variants: {
      heading1: {
        fontSize: 42,
        lineHeight: '52px',
      },
      heading2: {
        fontSize: 36,
        lineHeight: '44px',
      },
      heading3: {
        fontSize: 30,
        lineHeight: '38px',
      },
      heading4: {
        fontSize: 24,
        lineHeight: '32px',
      },
      'body-regular': {
        fontSize: 16,
        lineHeight: '24px',
      },
      'body-small': {
        fontSize: 14,
        lineHeight: '22px',
      },
      button: {
        textTransform: 'uppercase',
      },
    },
  },
  fonts: {
    url: 'https://cdn.shapermint.com/assets/fonts/shapermint',
    config: [
      {
        family: 'AvenirNextLTPro',
        src: 'AvenirNextLTPro-Regular.woff2',
        weight: 'normal',
      },
      {
        family: 'AvenirNextLTPro',
        src: 'AvenirNextLTPro-Demi.otf',
        weight: 600,
      },
      {
        family: 'AvenirNextLTPro',
        src: 'AvenirNextLTPro-Bold.otf',
        weight: 700,
      },
      {
        family: 'AvenirNextLTPro',
        src: 'AvenirNextLTPro-Heavy.otf',
        weight: '800 900',
      },
    ],
  },
  assets: {
    cdn: 'https://cdn.shapermint.com/assets/common',
    images: {
      favicon:
        'https://cdn.shopify.com/s/files/1/0021/4889/2732/files/Favicon_pink_196x_fedce07d-d19c-4ca4-a53a-5a55869cfc1e.png?v=1619463864',
      logo: 'https://cdn.shapermint.com/assets/images/shapermint/shapermint_logo_black.svg',
      logoSize: {
        width: '204',
        height: '35',
      },
    },
  },
  label: {
    member: 'CLUB MEMBERS',
  },
  space: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
    '4xl': '6rem',    // 96px
    '5xl': '50rem',   // 800px - for large containers
  },
  shadow: {
    xs: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    sm: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
  },
};

export type ShapermintTheme = typeof shapermintTheme;
